/** Human-readable collection URLs; Shopify requests still use native filter IDs. */
class FacetURL {
  static fields = new Map([
    ['gemstone', 'filter.p.m.custom.gemstones'],
    ['meaning', 'filter.p.m.custom.test_this'],
    ['shape', 'filter.p.m.custom.shapes'],
    ['material', 'filter.v.option.material']
  ]);
  static materialSlugs = new Map([
    ['925 Sterling Silver', 'silver'],
    ['18KT Yellow Gold Vermeil', 'yellowgoldvermeil'],
    ['18KT Rose Gold Vermeil', 'rosegoldvermeil']
  ]);
  static values = new Map();
  static enabled = /\/collections\//.test(window.location.pathname);

  static slug(label) {
    return label.normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9-]/g, '');
  }

  static collect(root = document) {
    for (const [alias, name] of this.fields) {
      if (!this.values.has(alias)) this.values.set(alias, new Map());
      const values = this.values.get(alias);
      root.querySelectorAll(`input[type="checkbox"][name="${name}"]`).forEach(input => {
        const label = input.parentElement.querySelector('.filter-name');
        const slug = (alias === 'material' && this.materialSlugs.get(input.value)) || (label && this.slug(label.textContent.trim()));
        if (slug) values.set(input.value, slug);
      });
    }
  }

  static decode(query) {
    const params = new URLSearchParams(query);
    const unresolved = [];
    if (!this.enabled) return { params, unresolved };
    for (const [alias, name] of this.fields) {
      const key = `filters[${alias}]`;
      if (!params.has(key)) continue;
      const values = this.values.get(alias) || new Map();
      const selected = params.getAll(key).flatMap(value => value.split('_')).filter(Boolean);
      const resolved = [];
      for (const slug of selected) {
        const matches = [...values].filter(([, value]) => value === slug);
        if (matches.length === 1) resolved.push(matches[0][0]);
        else unresolved.push(`${alias}:${slug}`);
      }
      if (unresolved.length) continue;
      params.delete(key);
      for (const value of resolved) {
        if (!params.getAll(name).includes(value)) params.append(name, value);
      }
    }
    return { params, unresolved };
  }

  static encode(query) {
    const params = new URLSearchParams(query);
    if (!this.enabled) return params.toString();
    for (const [alias, name] of this.fields) {
      const selected = params.getAll(name);
      if (!selected.length) continue;
      const values = this.values.get(alias) || new Map();
      const slugs = selected.map(value => values.get(value));
      // Ambiguous or unavailable names retain their working Shopify URLs.
      if (slugs.some(slug => !slug || [...values.values()].filter(value => value === slug).length !== 1)) continue;
      params.delete(name);
      params.set(`filters[${alias}]`, [...new Set(slugs)].join('_'));
    }
    return params.toString().replace(/filters%5B(gemstone|meaning|shape|material)%5D/g, 'filters[$1]');
  }

  static path(query) {
    const encoded = this.encode(query);
    return `${window.location.pathname}${encoded ? '?' + encoded : ''}${window.location.hash}`;
  }

  static async init() {
    if (!this.enabled || !document.getElementById('ProductGridContainer')) return;
    this.collect();
    const raw = window.location.search.slice(1);
    const hasAliases = [...this.fields.keys()].some(alias => new URLSearchParams(raw).has(`filters[${alias}]`));
    if (!hasAliases) {
      history.replaceState({ ...history.state, searchParams: raw }, '', this.path(raw));
      return;
    }
    const container = document.getElementById('ProductGridContainer');
    const products = container.querySelector('.collection-content');
    container.setAttribute('aria-busy', 'true');
    if (products) products.style.visibility = 'hidden';
    try {
      let decoded = this.decode(raw);
      if (decoded.unresolved.length) {
        // Other filters may have excluded a requested value from the initial HTML.
        const baseline = new URLSearchParams(raw);
        for (const key of [...baseline.keys()]) {
          if (key.startsWith('filter.') || key.startsWith('filters[') || key === 'page') baseline.delete(key);
        }
        baseline.set('section_id', document.getElementById('product-grid').dataset.id);
        const response = await fetch(`${window.location.pathname}?${baseline}`);
        if (!response.ok) throw new Error('Could not load filter values');
        this.collect(new DOMParser().parseFromString(await response.text(), 'text/html'));
        decoded = this.decode(raw);
      }
      if (decoded.unresolved.length) throw new Error('Unknown filter name');
      const nativeQuery = decoded.params.toString();
      FacetFiltersForm.searchParamsInitial = nativeQuery;
      history.replaceState({ ...history.state, searchParams: nativeQuery }, '', this.path(nativeQuery));
      await FacetFiltersForm.renderPage(nativeQuery, null, false);
    } catch (error) {
      const notice = document.createElement('p');
      notice.setAttribute('role', 'alert');
      notice.textContent = 'This filter link could not be applied. Please select your filters again.';
      container.prepend(notice);
    } finally {
      container.removeAttribute('aria-busy');
      const content = container.querySelector('.collection-content');
      if (content) content.style.visibility = '';
    }
  }
}

/**
 *  @class
 *  @function FacetsToggle
 */
class FacetToggle extends HTMLElement {

  constructor() {
    super();
    this.init();
  }
  init() {
    this.drawer = document.getElementById('Facet-Drawer');
    this.container = this.closest('.facets--bar-sticky');

    // Add functionality to buttons
    this.addEventListener('click', (e) => {
      e.preventDefault();
      document.body.classList.toggle('open-cc');
      this.drawer.classList.toggle('active');
    });
    this.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.click();
      }
    });
    if (this.container) {
      this.enableSticky();
    }

  }
  enableSticky() {
    let rootMargin = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height'), 10) + 1;
    this.observer = new IntersectionObserver(
      ([e]) => {
        e.target.classList.toggle('is-pinned', e.intersectionRatio <= 1 && e.intersectionRatio > 0);
      },
      {
        threshold: [1],
        rootMargin: `-${rootMargin}px 0px 0px 0px`
      }
    );

    this.observer.observe(this.container);
  }

}
customElements.define('facet-toggle', FacetToggle);

/**
 * Mobile collection toolbar sort control.
 * Keeps the compact native selector in sync with the existing AJAX facet form.
 */
class MobileFacetSort extends HTMLElement {
  constructor() {
    super();
    this.select = this.querySelector('select');
    if (!this.select) return;

    this.select.addEventListener('change', (event) => {
      const form = this.closest('facet-filters-form')?.querySelector('#FacetFiltersForm-bar');
      const sortInput = form?.querySelector('#SortBy');
      if (!sortInput) return;

      sortInput.value = event.target.value;
      sortInput.dispatchEvent(new Event('input', { bubbles: true }));
    });
  }
}

customElements.define('mobile-facets-sort', MobileFacetSort);

/**
 *  @class
 *  @function FacetFiltersForm
 */
class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      if (event.target.id === 'compare_toggle') {
        return;
      }
      this.onSubmitHandler(event);
    }, 500);

    this.querySelector('form').addEventListener('input', this.debouncedOnSubmit.bind(this));
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state?.searchParams ?? FacetURL.decode(window.location.search).params.toString();
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const renderId = ++FacetFiltersForm.renderId;
    const sections = FacetFiltersForm.getSections();
    const container = document.getElementsByClassName('thb-filter-count');
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');

    for (var item of container) {
      item.classList.add('loading');
    }

    const requests = sections.map((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = element => element.url === url;

      if (FacetFiltersForm.filterData.some(filterDataUrl)) {
        return FacetFiltersForm.renderSectionFromCache(filterDataUrl, event);
      } else {
        return FacetFiltersForm.renderSectionFromFetch(url, event, renderId);
      }
    });
    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
    return Promise.all(requests).catch(() => {
      // Native URLs remain a full-page fallback if an AJAX request fails.
      if (renderId === FacetFiltersForm.renderId) window.location.assign(`${window.location.pathname}?${searchParams}`);
    });
  }

  static renderSectionFromFetch(url, event, renderId) {
    return fetch(url)
      .then(response => {
        if (!response.ok) throw new Error('Filter request failed');
        return response.text();
      })
      .then((responseText) => {
        const html = responseText;
        if (!new DOMParser().parseFromString(html, 'text/html').getElementById('ProductGridContainer')) {
          throw new Error('Missing filter results');
        }
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, {
          html,
          url
        }];
        if (renderId !== FacetFiltersForm.renderId) return;
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductGridContainer').innerHTML;
  }

  static renderProductCount(html) {
    const countHtml = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount');
    const container = document.getElementsByClassName('thb-filter-count');

    if (countHtml) {
      for (var item of container) {
        item.innerHTML = countHtml.innerHTML;
        item.classList.remove('loading');
      }
    }

  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    FacetURL.collect(parsedHTML);

    const facetDetailsElements =
      parsedHTML.querySelectorAll('#FacetFiltersForm collapsible-row, #FacetFiltersFormMobile collapsible-row');
    const matchesIndex = (element) => {
      const jsFilter = event ? event.target.closest('collapsible-row') : undefined;
      return jsFilter ? element.dataset.index === jsFilter.dataset.index : false;
    };
    const facetsToRender = Array.from(facetDetailsElements).filter(element => !matchesIndex(element));
    const countsToRender = Array.from(facetDetailsElements).find(matchesIndex);

    facetsToRender.forEach((element) => {
      document.querySelector(`collapsible-row[data-index="${element.dataset.index}"]`).innerHTML = element.innerHTML;
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) FacetFiltersForm.renderCounts(countsToRender, event.target.closest('collapsible-row'));
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-filter-count'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

  }

  static renderCounts(source, target) {
    const targetElement = target.querySelector('.facets__selected');
    const sourceElement = source.querySelector('.facets__selected');

    if (sourceElement && targetElement) {
      target.querySelector('.facets__selected').outerHTML = source.querySelector('.facets__selected').outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({
      searchParams
    }, '', FacetURL.path(searchParams));
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      }];
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const formData = new FormData(event.target.closest('form'));
    const searchParams = new URLSearchParams(formData);
    const currentSort = new URLSearchParams(FacetFiltersForm.searchParamsPrev).get('sort_by');
    if (!searchParams.has('sort_by') && currentSort) searchParams.set('sort_by', currentSort);
    if (searchParams.get('filter.v.price.gte') === "0.00") {
      searchParams.delete('filter.v.price.gte');
    }
    if (document.querySelector('.price_slider')) {
      if (searchParams.get('filter.v.price.lte') === parseFloat(document.querySelector('.price_slider').dataset.max).toFixed(2)) {
        searchParams.delete('filter.v.price.lte');
      }
    }
    FacetFiltersForm.renderPage(searchParams.toString(), event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url = event.currentTarget.href.indexOf('?') == -1 ? '' : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.renderId = 0;
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

/**
 *  @class
 *  @function Facetremove
 */
class FacetRemove extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('a').forEach((item) => {
      item.addEventListener('click', (event) => {
        event.preventDefault();
        const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
        form.onActiveFilterClick(event);
      });
    });
  }
}

customElements.define('facet-remove', FacetRemove);

/**
 *  @class
 *  @function FacetToolbar
 */
class FacetToolbar extends HTMLElement {
  constructor() {
    super();
  }
  connectedCallback() {
    this.compareToggle = this.querySelector('#compare_toggle');
    if (this.compareToggle) {
      this.compareToggle.addEventListener('change', this.onCompare);
    }
  }
  onCompare(e) {
    document.body.classList.toggle('compare-true');
    e.preventDefault();
    return false;
  }
}
customElements.define('facet-toolbar', FacetToolbar);

/**
 *  @class
 *  @function PriceSlider
 */
class PriceSlider extends HTMLElement {

  constructor() {
    super();
  }
  connectedCallback() {
    let slider = this.querySelector('.price_slider'),
      amounts = this.querySelector('.price_slider_amount'),
      args = {
        start: [parseFloat(slider.dataset.minValue || 0), parseFloat(slider.dataset.maxValue || slider.dataset.max)],
        connect: true,
        step: 10,
        direction: document.dir,
        handleAttributes: [
          { 'aria-label': 'lower' },
          { 'aria-label': 'upper' },
        ],
        range: {
          'min': 0,
          'max': parseFloat(slider.dataset.max)
        }
      },
      event = new CustomEvent('input'),
      form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');

    if (slider.classList.contains('noUi-target')) {
      slider.noUiSlider.destroy();
    }
    noUiSlider.create(slider, args);

    slider.noUiSlider.on('update', function (values) {
      amounts.querySelector('.field__input_min').value = values[0];
      amounts.querySelector('.field__input_max').value = values[1];
    });
    slider.noUiSlider.on('change', function (values) {
      form.querySelector('form').dispatchEvent(event);
    });
  }
}
customElements.define('price-slider', PriceSlider);

(() => {
  const CLASS_NAME = 'facets-sidebar-hidden';
  const TOOLBAR_SELECTOR = '.collection-container--desktop-layout .sidebar-container.facets--sidebar .collection-toolbar';
  const GRID_CONTAINER_ID = 'ProductGridContainer';
  let toolbarResizeObserver = null;
  let gridMutationObserver = null;
  let rafId = 0;

  const setToolbarHeightVar = () => {
    const toolbar = document.querySelector(TOOLBAR_SELECTOR);
    if (!toolbar) {
      document.documentElement.style.removeProperty('--collection-toolbar-height');
      return;
    }
    const height = toolbar.getBoundingClientRect().height;
    document.documentElement.style.setProperty('--collection-toolbar-height', `${height}px`);
  };

  const scheduleToolbarHeightUpdate = () => {
    if (rafId) {
      cancelAnimationFrame(rafId);
    }
    rafId = requestAnimationFrame(() => {
      rafId = 0;
      setToolbarHeightVar();
    });
  };

  const attachToolbarResizeObserver = () => {
    if (toolbarResizeObserver) {
      toolbarResizeObserver.disconnect();
      toolbarResizeObserver = null;
    }
    const toolbar = document.querySelector(TOOLBAR_SELECTOR);
    if (!toolbar || !('ResizeObserver' in window)) {
      scheduleToolbarHeightUpdate();
      return;
    }
    toolbarResizeObserver = new ResizeObserver(() => {
      scheduleToolbarHeightUpdate();
    });
    toolbarResizeObserver.observe(toolbar);
    scheduleToolbarHeightUpdate();
  };

  const attachGridMutationObserver = () => {
    if (gridMutationObserver) {
      gridMutationObserver.disconnect();
      gridMutationObserver = null;
    }
    const grid = document.getElementById(GRID_CONTAINER_ID);
    if (!grid || !('MutationObserver' in window)) return;
    gridMutationObserver = new MutationObserver(() => {
      updateAllToggleButtons();
      attachToolbarResizeObserver();
    });
    gridMutationObserver.observe(grid, { childList: true, subtree: true });
  };

  const updateToggleButton = (button) => {
    if (!button) return;
    const isHidden = document.body.classList.contains(CLASS_NAME);
    const textEl = button.querySelector('[data-facets-sidebar-toggle-text]');
    const iconEl = button.querySelector('[data-facets-sidebar-toggle-icon]');
    const hideLabel = button.getAttribute('data-label-hide') || 'HIDE FILTERS';
    const showLabel = button.getAttribute('data-label-show') || 'SHOW FILTERS';

    if (textEl && textEl.textContent !== (isHidden ? showLabel : hideLabel)) {
      textEl.textContent = isHidden ? showLabel : hideLabel;
    }
    if (iconEl && iconEl.textContent !== (isHidden ? '+' : '—')) {
      iconEl.textContent = isHidden ? '+' : '—';
    }
    button.setAttribute('aria-expanded', isHidden ? 'false' : 'true');
  };

  const updateAllToggleButtons = () => {
    document.querySelectorAll('[data-facets-sidebar-toggle]').forEach((button) => {
      updateToggleButton(button);
    });
  };
  const initStickyOffsets = () => {
    attachToolbarResizeObserver();
    attachGridMutationObserver();
  };

  document.body.classList.remove(CLASS_NAME);
  updateAllToggleButtons();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initStickyOffsets, { once: true });
  } else {
    initStickyOffsets();
  }

  window.addEventListener(
    'resize',
    () => {
      attachToolbarResizeObserver();
    },
    { passive: true }
  );

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-facets-sidebar-toggle]');
    if (!button) return;
    event.preventDefault();

    const isHidden = !document.body.classList.contains(CLASS_NAME);
    document.body.classList.toggle(CLASS_NAME, isHidden);
    updateAllToggleButtons();
  });
})();

// Run after the custom elements have been registered, including removable chips.
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => FacetURL.init(), { once: true });
} else {
  FacetURL.init();
}
